import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import styles from "../../form.module.css";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Spin,
  Typography,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import {
  eAnswerType,
  eFORM_FOR,
  eFORMSTATUS,
  eResultCode,
  eSTAGE,
  eUSER_FORM_TYPE,
  eYES_OR_NO,
  INPUT_LENGTH,
} from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import Text from "../../../../context/language-context";
import { Table } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  GetFormData,
  GetSpecificUserData,
  updateField,
} from "../../../../utils/api.constant";
import { FaCheck } from "react-icons/fa";
import { LoaderContext } from "../../../../context/loader";
import { Rule } from "antd/es/form";
import {
  getNumberInLanguage,
  stringDateInLanguage,
} from "../../../../utils/helperFunctions";
import EncryptUtils from "../../../../utils/encrypt";
import {
  allowOnlyNumericInput_onKeyDown,
  disableFutureDates_on_input_date_max,
  numberInputRules,
} from "../../../../utils/input.validator";
import { Regex } from "../../../../utils/validation";
import { TFormProps } from "../../../../types";
import { UserContext } from "../../../../context";
import { DocumentUploadField } from "../../../../components/document-upload";
import Utils from "../../../../utils";
import FormHelpTooltip from "../../../../components/common/FormHelpTooltip";
import { CURRENT_YEAR } from "../../../../utils/constants";
import { useFormAutoSave } from "../../../../hooks/useFormAutoSave";
import { SCHOOL_PT2_Q2 } from "../../../../types/table_types";

// Define all Date field keys for this form to enable robust date conversion
const DATE_FIELD_KEYS = [
  "SCHOOL_2026_PT1_Q7", // Date of commencement
  "SCHOOL_2026_PT1_Q8_A", // School closed - From
  "SCHOOL_2026_PT1_Q8_B", // School closed - To
  "SCHOOL_2026_PT1_Q21", // Affidavit Date
];

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

/**
 * SchoolPartOne component is a form for collecting information about a school.
 * It includes various fields for inputting details about the school, its head,
 * trustees, sponsors, and other relevant information.
 *
 * @param {TFormProps} props - The properties passed to the component.
 * @param {boolean} props.disabled - Indicates if the form fields should be disabled.
 * @param {string} props.currentFormId - The current form ID.
 * @param {string} props.currentUfmId - The current user form mapping ID.
 *
 * @returns {JSX.Element} The rendered SchoolPartOne component.
 *
 * @component
 * @example
 * <SchoolPartOne disabled={false} currentFormId="form123" currentUfmId="ufm123" />
 *
 * @remarks
 * This component uses various hooks and contexts such as `useFetch`, `useToast`,
 * `useSearchParams`, `useNavigate`, `UserContext`, `LanguageContext`, and `LoaderContext`.
 * It also includes multiple `useEffect` hooks to handle side effects and form data fetching.
 *
 * The form includes fields for:
 * - School name and address
 * - Head of the institution's details
 * - Trustee and sponsor details
 * - Various tables for inputting data related to teachers, students, and achievements
 * - An affidavit declaration section
 *
 * The form submission is handled by the `onFinish` function, which sends the form data
 * to the server using the `post` method from `useFetch`. The `handleFieldChange` function
 * is used to handle changes to individual form fields and update the server accordingly.
 *
 * The component also includes functions for uploading documents and calculating totals
 * for table fields.
 */

export type DATA_TYPE_SCHOOL_PT1_Q9 = {
  key: any;
  label: string;
  ved: string;
  vedShakha1: string;
  vedShakha2?: string;
  vedShakha3?: string;
  vedShakha4?: string;
};

/**
 * Form data type for SCHOOL_PT1_Q9 - array-based structure
 * Uses vedIndex to reference DATA_SOURCE_SCHOOL_PT1_Q9
 */
export type FORM_DATA_SCHOOL_PT1_Q9 = {
  vedIndex: number;
  selectedShakhas: string[];
  anyaDetails: string;
};

export type DATA_TYPE_SCHOOL_PT1_Q10 = {
  vedShakha: string;
  fullTimeTeacher: string | number;
  partTimeTeacher: string | number;
  totalTeacher: string | number;
};

export type DATA_TYPE_SCHOOL_PT1_Q12_A = {
  vedShakha: string;
  moolantStudents: string | number;
  level1Students: string | number;
  level2Students: string | number;
  beyondLevel2Students: string | number;
  totalVedStudents: string | number;
};

export type DATA_TYPE_SCHOOL_PT1_Q12_B = {
  studentStartSession: string | number;
  studentCompletedStudy: string | number;
  newVedicStudent: string | number;
  studentDroppedOut: string | number;
  studentEndSession: string | number;
};

export type DATA_TYPE_SCHOOL_PT1_Q12_C = {
  level: any;
  moolpathi: number | string;
  level1: number | string;
  level2: number | string;
  beyondLevel2: number | string;
  total: number | string;
};

/**
 * Initial data for SCHOOL_PT1_Q9 - array-based structure
 * Each item corresponds to a Ved (indexed 0-4)
 */
const INITIAL_SCHOOL_PT1_Q9_DATA: FORM_DATA_SCHOOL_PT1_Q9[] = [
  { vedIndex: 0, selectedShakhas: [], anyaDetails: "" },
  { vedIndex: 1, selectedShakhas: [], anyaDetails: "" },
  { vedIndex: 2, selectedShakhas: [], anyaDetails: "" },
  { vedIndex: 3, selectedShakhas: [], anyaDetails: "" },
  { vedIndex: 4, selectedShakhas: [], anyaDetails: "" },
];

const SchoolPartOneCurrent = ({
  disabled,
  currentFormId,
  currentUserId,
  currentFormFor,
}: TFormProps) => {
  const [form] = Form.useForm();
  // Watch affidavitAccepted to trigger re-renders when checkbox changes
  const affidavitAccepted = Form.useWatch("affidavitAccepted", form);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formId, setFormId] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const { user } = useContext(UserContext);
  const [documentsData, setDocumentsData] = useState<any>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | number | null>(null);
  const navigate = useNavigate();
  const [isReviewMode, setIsReviewMode] = useState(false);

  // Loader Context For show Loader
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;
  const OTHER = Text({ tid: "ANYA", def: "ANYA" });
  const SELECT_AT_LEAST_ONE_MSG = Text({
    tid: "REQUIRED_SELECT_AT_LEAST_ONE",
    def: "Please select at least one option.",
  });

  useEffect(() => {
    if (searchParams) {
      // Decrypt and set formId and formFor from URL search parameters
      const formId = EncryptUtils.decrypt(searchParams.get("id") || "");
      const formFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      const uId = EncryptUtils.decrypt(searchParams.get("ui") || "");
      console.log("formId", formId);
      console.log("formFor", formFor);
      console.log("uId", uId);
      setFormId(formId);
      setFormFor(formFor);
      setUserId(uId);
    }
    if (currentFormId) {
      // Decrypt and set formId from props if available
      setFormId(EncryptUtils.decrypt(currentFormId));
    }
    if (currentUserId) {
      setUserId(EncryptUtils.decrypt(currentUserId));
    }
    if (currentFormFor) {
      setFormFor(EncryptUtils.decrypt(currentFormFor));
    }
  }, [searchParams, currentFormId, currentUserId, currentFormFor]);

  const handleFieldChange = async (
    field: string,
    value: any,
    answerType: number = eAnswerType.TEXT,
    bulkPayload?: any[],
    isSubmitted?: boolean,
  ) => {
    if (formId?.length) {
      try {
        setIsSubmitting(true);

        const answers: any[] = [];

        // Handle single field
        if (field.length > 0) {
          const trimmedValue = typeof value === "string" ? value.trim() : value;
          if (trimmedValue === null || trimmedValue === undefined) {
            form.setFieldValue(field, "");
            // Continue execution to save the empty string to backend
          }

          // Auto-detect answer type from JSON strings
          let detectedType = answerType;
          if (typeof trimmedValue === "string") {
            if (trimmedValue.startsWith("[")) {
              detectedType = eAnswerType.ARRAY;
            } else if (trimmedValue.startsWith("{")) {
              detectedType = eAnswerType.OBJECT;
            }
          }

          // Format answer based on type
          let formattedAnswer = trimmedValue;
          if (
            detectedType === eAnswerType.ARRAY ||
            detectedType === eAnswerType.OBJECT
          ) {
            formattedAnswer =
              typeof trimmedValue === "string"
                ? trimmedValue
                : JSON.stringify(trimmedValue);
          } else if (
            detectedType === eAnswerType.DATE &&
            typeof trimmedValue === "object" &&
            trimmedValue?.format
          ) {
            formattedAnswer = trimmedValue.format("YYYY-MM-DD");
          }

          answers.push({
            questionKey: field,
            answerType: detectedType,
            answer: formattedAnswer,
            part: eSTAGE.STAGE1,
          });
        }

        // Handle bulk payload (multiple answers)
        if (bulkPayload && Array.isArray(bulkPayload)) {
          answers.push(...bulkPayload);
        }

        const payload: any = {
          data: {
            id: formId,
            answers: answers,
          },
        };

        if (isSubmitted) {
          payload.data.isSubmitted = true;
        }

        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode == eResultCode.SUCCESS) {
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Initialize auto-save hook
  const { debouncedSave, handleFormSubmit, onFormValuesChange } =
    useFormAutoSave({
      formId,
      form,
      stage: eSTAGE.STAGE1,
      handleFieldChange,
      excludeFields: ["SCHOOL_2026_PT1_Q23"],
    });

  // Centralized handler for local side-effects + auto-save
  const handleLocalFormChange = (changedValues: any, allValues: any) => {
    // 1. Run Auto Save Logic from hook
    onFormValuesChange(changedValues, allValues);

    // 2. Handle Side Effects

    // Q2_A Side Effect: Copy Name to Q18 and Q20
    if ("SCHOOL_2026_PT1_Q2_A" in changedValues) {
      const value = changedValues.SCHOOL_2026_PT1_Q2_A;
      // We only update if value exists/changed.
      if (value) {
        form.setFieldValue("SCHOOL_2026_PT1_Q18", value);
        form.setFieldValue("SCHOOL_2026_PT1_Q20", value);
        debouncedSave("SCHOOL_2026_PT1_Q18", value);
        debouncedSave("SCHOOL_2026_PT1_Q20", value);
      }
    }

    // Q8 Side Effect: Clear Q8_A and Q8_B if "No"
    if ("SCHOOL_2026_PT1_Q8" in changedValues) {
      if (changedValues.SCHOOL_2026_PT1_Q8 === eYES_OR_NO.NO) {
        form.setFieldValue("SCHOOL_2026_PT1_Q8_A", null);
        form.setFieldValue("SCHOOL_2026_PT1_Q8_B", null);
        // Save empty strings to backend
        debouncedSave("SCHOOL_2026_PT1_Q8_A", "");
        debouncedSave("SCHOOL_2026_PT1_Q8_B", "");
      }
    }
  };

  /**
   * Handle "Next" button click - validates form and enters review mode
   */
  const handleNextClick = async () => {
    try {
      await form.validateFields();
      setIsReviewMode(true);
      // Scroll to top so user can review from beginning
      document.getElementById("schoolFormPart1")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } catch (errorInfo: any) {
      const firstError = errorInfo?.errorFields?.[0];

      if (firstError) {
        const fieldName = firstError.name?.[0];
        if (fieldName === "SCHOOL_2026_PT1_Q23") {
          const element = document.getElementById("SCHOOL_2026_PT1_Q23");
          element?.focus?.();
          element?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return;
        }

        form.scrollToField(firstError.name, {
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  /**
   * Handle "Back" button click - exits review mode to allow editing
   */
  const handleBackClick = () => {
    setIsReviewMode(false);
  };

  useEffect(() => {
    const currentValues = form.getFieldsValue(true) as any;
    form.setFieldsValue({
      SCHOOL_2026_PT1_Q9: INITIAL_SCHOOL_PT1_Q9_DATA,
      SCHOOL_2026_PT1_Q10: DATA_SOURCE_SCHOOL_PT1_Q10,
      SCHOOL_2026_PT1_Q12_A: DATA_SOURCE_SCHOOL_PT1_Q12_A,
      SCHOOL_2026_PT1_Q12_B: DATA_SOURCE_SCHOOL_PT1_Q12_B,
      SCHOOL_2026_PT1_Q12_C: DATA_SOURCE_SCHOOL_PT1_Q12_C,
      SCHOOL_2026_PT1_Q21: currentValues?.SCHOOL_2026_PT1_Q21 || dayjs(),
    });
  }, [form]);

  const inputRequiredBasicRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];

  // Q9 ---------------------------Q9-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------

  /**
   * Handle checkbox change for Q9 - array-based approach
   * @param vedIndex - Index of the Ved in the array (0-4)
   * @param shakha - The shakha text being selected/deselected
   * @param checked - Whether the checkbox is checked
   */
  const onCheckboxChange = (
    vedIndex: number,
    shakha: string,
    checked: boolean,
  ) => {
    const currentData: FORM_DATA_SCHOOL_PT1_Q9[] =
      form.getFieldValue("SCHOOL_2026_PT1_Q9") || INITIAL_SCHOOL_PT1_Q9_DATA;

    // Clone the array to avoid mutation
    const updatedData = currentData.map((item, index) => {
      if (index !== vedIndex) return { ...item };

      let updatedShakhas = [...(item.selectedShakhas || [])];

      if (checked) {
        if (!updatedShakhas.includes(shakha)) {
          updatedShakhas.push(shakha);
        }
      } else {
        updatedShakhas = updatedShakhas.filter((s) => s !== shakha);
      }

      // Clear anyaDetails if "ANYA" is unchecked
      const hasAnyaSelected = updatedShakhas.some(
        (s) => s === "ANYA" || s === "अन्य" || s.includes("अन्य"),
      );

      return {
        ...item,
        selectedShakhas: updatedShakhas,
        anyaDetails: hasAnyaSelected ? item.anyaDetails : "",
      };
    });

    form.setFieldsValue({
      SCHOOL_2026_PT1_Q9: updatedData,
    });

    handleFieldChange(
      "SCHOOL_2026_PT1_Q9",
      JSON.stringify(updatedData),
      eAnswerType.ARRAY,
    );
  };

  /**
   * Maps previous year (2025) form data to current year (2026) field structure
   * Handles field renaming, type conversions (dates, arrays, objects), and null values
   */
  const getMappedData = (data: any) => {
    if (!data) return {};

    const mappedData: { [key: string]: any } = {
      // Basic Info - Q1
      SCHOOL_2026_PT1_Q1_A: data.SCHOOL_PT1_Q1_NAME || "",
      SCHOOL_2026_PT1_Q1_B: data.SCHOOL_PT1_Q1_ADDRESS || "",

      // Head of Institution - Q2
      SCHOOL_2026_PT1_Q2_A: data.SCHOOL_PT1_Q2 || "",
      SCHOOL_2026_PT1_Q2_B: data.SCHOOL_PT1_Q3 || "",
      SCHOOL_2026_PT1_Q2_C: data.SCHOOL_PT1_Q4 || "",
      SCHOOL_2026_PT1_Q2_D: data.SCHOOL_PT1_Q5 || "",

      // Website - Q3
      SCHOOL_2026_PT1_Q3: data.SCHOOL_PT1_Q6 || "",

      // Contact Person - Q4
      SCHOOL_2026_PT1_Q4_A: data.SCHOOL_PT1_Q7 || "",
      SCHOOL_2026_PT1_Q4_B: data.SCHOOL_PT1_Q8 || "",
      SCHOOL_2026_PT1_Q4_C: data.SCHOOL_PT1_Q9 || "",
      SCHOOL_2026_PT1_Q4_D: data.SCHOOL_PT1_Q10 || "",

      // Language - Q5
      SCHOOL_2026_PT1_Q5: data.SCHOOL_PT1_Q11 || "",

      // Founders Details - Q6 (merged from Q13-Q17 and Q15-Q16)
      SCHOOL_2026_PT1_Q6_A: data.SCHOOL_PT1_Q12 || "", // Institution type (if exists)
      SCHOOL_2026_PT1_Q6_B: data.SCHOOL_PT1_Q13 || "",
      SCHOOL_2026_PT1_Q6_C: data.SCHOOL_PT1_Q14 || "",
      SCHOOL_2026_PT1_Q6_D: data.SCHOOL_PT1_Q15 || "",
      SCHOOL_2026_PT1_Q6_E: data.SCHOOL_PT1_Q15 || "", // Address (if needed)
      SCHOOL_2026_PT1_Q6_F: data.SCHOOL_PT1_Q16 || "",
      SCHOOL_2026_PT1_Q6_G: data.SCHOOL_PT1_Q17 || "",

      // Date of commencement - Q7
      SCHOOL_2026_PT1_Q7: Utils.parseToDayjs(data.SCHOOL_PT1_Q27),

      // School closed - Q8
      SCHOOL_2026_PT1_Q8: data.SCHOOL_PT1_Q28 || "",
      SCHOOL_2026_PT1_Q8_A: data.SCHOOL_PT1_Q28_A
        ? Array.isArray(data.SCHOOL_PT1_Q28_A)
          ? data.SCHOOL_PT1_Q28_A
          : JSON.parse(data.SCHOOL_PT1_Q28_A)
        : null,
      SCHOOL_2026_PT1_Q8_B: data.SCHOOL_PT1_Q28_B
        ? Array.isArray(data.SCHOOL_PT1_Q28_B)
          ? data.SCHOOL_PT1_Q28_B
          : JSON.parse(data.SCHOOL_PT1_Q28_B)
        : null,

      // Ved Shakhas - Q9
      SCHOOL_2026_PT1_Q9: data.SCHOOL_PT1_Q29
        ? typeof data.SCHOOL_PT1_Q29 === "object"
          ? data.SCHOOL_PT1_Q29
          : JSON.parse(data.SCHOOL_PT1_Q29)
        : {},

      // Teachers - Q10
      SCHOOL_2026_PT1_Q10: data.SCHOOL_PT1_Q30
        ? Array.isArray(data.SCHOOL_PT1_Q30)
          ? data.SCHOOL_PT1_Q30
          : JSON.parse(data.SCHOOL_PT1_Q30)
        : [],

      // Ved Subjects - Q11
      SCHOOL_2026_PT1_Q11: data.SCHOOL_PT1_Q31 || "",
      // Students - Q12
      SCHOOL_2026_PT1_Q12_A: data.SCHOOL_PT1_Q32
        ? Array.isArray(data.SCHOOL_PT1_Q32)
          ? data.SCHOOL_PT1_Q32
          : JSON.parse(data.SCHOOL_PT1_Q32)
        : [],

      // Q12_B: Special handling for historical year data
      // Previous year has 5 rows (2025, 2024, 2023, 2022, 2021)
      // Current year needs: [2026 (empty), 2025, 2024, 2023, 2022] - drop oldest (2021)
      SCHOOL_2026_PT1_Q12_B: (() => {
        if (!data.SCHOOL_PT1_Q33) return [];

        try {
          const previousYearData = Array.isArray(data.SCHOOL_PT1_Q33)
            ? data.SCHOOL_PT1_Q33
            : JSON.parse(data.SCHOOL_PT1_Q33);

          // Create new empty row for current year (2026)
          const newYearRow = {
            year: String(CURRENT_YEAR),
            studentStartSession: "",
            studentCompletedStudy: "",
            newVedicStudent: "",
            studentDroppedOut: "",
            studentEndSession: "",
          };

          // Take only first 4 rows from previous year (drop the 5th/oldest year)
          // This shifts data: 2025→row1, 2024→row2, 2023→row3, 2022→row4
          const shiftedData = previousYearData.slice(0, 4);

          return [newYearRow, ...shiftedData];
        } catch (error) {
          console.error("Error parsing Q12_B historical data:", error);
          return [];
        }
      })(),

      SCHOOL_2026_PT1_Q12_C: data.SCHOOL_PT1_Q34
        ? Array.isArray(data.SCHOOL_PT1_Q34)
          ? data.SCHOOL_PT1_Q34
          : JSON.parse(data.SCHOOL_PT1_Q34)
        : [],

      // Text Fields Q13-Q20
      SCHOOL_2026_PT1_Q13: data.SCHOOL_PT1_Q35 || "",
      SCHOOL_2026_PT1_Q14: data.SCHOOL_PT1_Q36 || "",
      SCHOOL_2026_PT1_Q15: data.SCHOOL_PT1_Q37 || "",
      SCHOOL_2026_PT1_Q16: data.SCHOOL_PT1_Q38 || "",
      SCHOOL_2026_PT1_Q17: data.SCHOOL_PT1_Q39 || "",
      SCHOOL_2026_PT1_Q18: data.SCHOOL_PT1_Q40 || "",
      SCHOOL_2026_PT1_Q19: data.SCHOOL_PT1_Q41 || "",
      SCHOOL_2026_PT1_Q20: data.SCHOOL_PT1_Q42 || "",

      // Date - Q21
      SCHOOL_2026_PT1_Q21: Utils.parseToDayjs(data.SCHOOL_PT1_Q44),

      // Final Field - Q22
      SCHOOL_2026_PT1_Q22: data.SCHOOL_PT1_Q45 || "",

      // Document Upload - Q23 (excluded from previous data)
      // SCHOOL_2026_PT1_Q23: "" // Don't copy file uploads
    };

    // Filter out null/undefined values to avoid overwriting existing data
    return Object.fromEntries(
      Object.entries(mappedData).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          part: eSTAGE.STAGE1,
          year: CURRENT_YEAR,
          userFormType: eUSER_FORM_TYPE.SCHOOL,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS && Array.isArray(data)) {
        // Convert array of answers to form field values
        const formattedData: { [key: string]: any } = {};
        const docs: any[] = [];

        data.forEach((answer: any) => {
          const { questionKey, answerType, answer: answerValue } = answer;

          if (!questionKey) return;

          // Parse based on answerType
          switch (answerType) {
            case eAnswerType.ARRAY:
            case eAnswerType.OBJECT:
              try {
                formattedData[questionKey] = answerValue
                  ? JSON.parse(answerValue)
                  : answerType === eAnswerType.ARRAY
                    ? []
                    : {};
              } catch (error) {
                console.error(`Error parsing ${questionKey}:`, error);
                formattedData[questionKey] =
                  answerType === eAnswerType.ARRAY ? [] : {};
              }
              break;
            case eAnswerType.FILE:
              // File fields store document ID, don't set in form
              formattedData[questionKey] = answerValue || "";
              if (answerValue) {
                docs.push({
                  docPath: answerValue,
                  labelName: questionKey, // linking doc to question
                });
              }
              break;
            case eAnswerType.DATE:
              // Convert date string to dayjs object for DatePicker
              formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              break;
            case eAnswerType.TEXT:
            default:
              formattedData[questionKey] = answerValue || "";

              // Force Date Conversion for known date fields if they come as TEXT
              if (DATE_FIELD_KEYS.includes(questionKey) && answerValue) {
                formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              }
              break;
          }
        });

        if (docs.length > 0) {
          setDocumentsData(docs);
        }

        if (Object.keys(formattedData).length > 0) {
          form.setFieldsValue(formattedData);

          // Sync affidavitAccepted state with loaded form data
          if (
            formattedData.affidavitAccepted ||
            formattedData.affidavitAccepted === "1"
          ) {
            form.setFieldValue("affidavitAccepted", true);
          }
        }

        // Handle documents separately if needed
        // setDocumentsData(data?.documents);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPreviousFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          part: eSTAGE.STAGE1,
          year: CURRENT_YEAR - 1,
          userFormType: eUSER_FORM_TYPE.SCHOOL,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        const formData = Object.values(data?.schoolPt1Forms || {}).every(
          (value) => value === null,
        )
          ? {}
          : data?.schoolPt1Forms || {};

        // List of fields to exclude (file input fields)
        const jsonStringFields = [
          "SCHOOL_PT1_Q28_A",
          "SCHOOL_PT1_Q28_B",
          "SCHOOL_PT1_Q29",
          "SCHOOL_PT1_Q30",
          "SCHOOL_PT1_Q32",
          "SCHOOL_PT1_Q33",
          "SCHOOL_PT1_Q34",
        ];

        const fileFields = ["SCHOOL_PT1_Q43"];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                // Special handling for SCHOOL_PT1_Q29 to ensure it's an object
                if (key === "SCHOOL_PT1_Q29") {
                  acc[key] = formData[key] ? JSON.parse(formData[key]) : {};
                } else {
                  acc[key] = formData[key] ? JSON.parse(formData[key]) : [{}];
                }
              } catch (error) {
                console.error(error);
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else {
              acc[key] = formData[key];
            }
            return acc;
          },
          {},
        );

        const mappedData = getMappedData(formattedData);

        console.log("Previous Year Data (2025):", formattedData);
        console.log("Mapped to 2026 Fields:", mappedData);

        // mappedData.SCHOOL_2026_PT1_Q23 = undefined;

        // // Q21 - Affidavit Date (use current date instead of previous year date)
        // mappedData.SCHOOL_2026_PT1_Q21 = dayjs();

        // // Q22 - Place (optional: clear if you want user to re-enter)
        // mappedData.SCHOOL_2026_PT1_Q22 = undefined;

        // // Affidavit acceptance checkbox should not be pre-selected
        // mappedData.affidavitAccepted = undefined;

        // Use mapped data (with 2026 field names) instead of original formattedData
        if (mappedData && Object.keys(mappedData).length > 0) {
          form.setFieldsValue(mappedData);
        }

        // Don't set documents from previous year - let current year handle it
        // setDocumentsData(data?.documents);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formId && formFor && userId) {
      const fetchData = async () => {
        await getPreviousFormData();
        await getFormData();

        let specificUserData: any = null;
        if (userId && Number(formFor) === eFORM_FOR.OTHER) {
          try {
            const userResponse = await post(GetSpecificUserData, {
              data: { id: userId },
            });
            if (
              userResponse?.dataResponse?.returnCode === eResultCode.SUCCESS &&
              userResponse?.data
            ) {
              specificUserData = userResponse.data;
            }
          } catch (error) {
            console.error("Error fetching specific user data:", error);
          }
        }

        const userDataToUse = specificUserData || user;

        // Auto-fill logic: Run AFTER fetching data to avoid overwriting existing user input.
        // For School form, we map user details to the "Contact Person" section (Q4).
        const autoFillData: { [key: string]: any } = {
          SCHOOL_2026_PT1_Q2_A: userDataToUse?.fullName, // Contact Person Name
          SCHOOL_2026_PT1_Q2_C: userDataToUse?.mobileNumber, // Mobile No.
          SCHOOL_2026_PT1_Q2_D: userDataToUse?.emailId, // Email ID
          SCHOOL_2026_PT1_Q4_A: userDataToUse?.fullName, // Contact Person Name
          SCHOOL_2026_PT1_Q4_C: userDataToUse?.mobileNumber, // Mobile No.
          SCHOOL_2026_PT1_Q4_D: userDataToUse?.emailId, // Email ID
          SCHOOL_2026_PT1_Q18: userDataToUse?.fullName, // Contact Person Name
          SCHOOL_2026_PT1_Q20: userDataToUse?.fullName, // Contact Person Name
        };

        const fieldsToSet: { [key: string]: any } = {};
        const bulkPayload: any[] = [];

        Object.entries(autoFillData).forEach(([key, value]) => {
          // Check if field is currently empty in the form
          const currentValue = form.getFieldValue(key);

          if (
            !currentValue &&
            value !== undefined &&
            value !== null &&
            value !== ""
          ) {
            // Set value for Form State
            fieldsToSet[key] = value;

            const answerStr = String(value);

            if (answerStr) {
              bulkPayload.push({
                questionKey: key,
                answerType: eAnswerType.TEXT,
                answer: answerStr,
                part: eSTAGE.STAGE1,
              });
            }
          }
        });

        if (bulkPayload.length > 0) {
          form.setFieldsValue(fieldsToSet);
          handleFieldChange("", "", eAnswerType.TEXT, bulkPayload);
        }
      };
      fetchData();
    }
  }, [formId, formFor, userId, user]);

  const calculateColumnTotals = (
    form: any,
    fieldName: string,
    fieldsToSum: string[],
    totalField: string,
    totalRowIndex: number,
  ) => {
    const currentData = form.getFieldValue(fieldName) || [];

    // Ensure currentData has the correct length
    if (currentData.length < totalRowIndex + 1) {
      // Fill missing rows with empty objects
      for (let i = currentData.length; i <= totalRowIndex; i++) {
        currentData[i] = {
          vedShakha: i === totalRowIndex ? "TOTAL_SUM" : "",
          fullTimeTeacher: "",
          partTimeTeacher: "",
          totalTeacher: "",
        };
      }
    }

    const totals: { [key: string]: number } = {};

    // Initialize totals
    fieldsToSum.forEach((field) => {
      totals[field] = 0;
    });

    // Calculate totals for each field
    currentData.forEach((row: any, index: number) => {
      if (index !== totalRowIndex) {
        fieldsToSum.forEach((field) => {
          totals[field] += parseInt(row[field] || 0);
        });
      }
    });

    // Update the total row
    const totalRow = currentData[totalRowIndex];
    if (totalRow) {
      fieldsToSum.forEach((field) => {
        totalRow[field] = totals[field].toString();
      });

      // Calculate the total for the total row
      const total = fieldsToSum.reduce((sum, field) => {
        return sum + parseInt(totalRow[field] || 0);
      }, 0);
      totalRow[totalField] = total.toString();

      // Update the form data
      form.setFieldsValue({ [fieldName]: currentData });
    }
  };

  const updateRowTotal = (
    form: any,
    fieldName: string,
    index: number,
    field: string,
    value: string,
    fieldsToSum: string[],
    totalField: string,
    totalRowIndex: number,
  ) => {
    const currentData = form.getFieldValue(fieldName) || [];
    const row = currentData[index];

    if (row) {
      // Update the field value
      row[field] = value;

      // Calculate the total for the current row
      const total = fieldsToSum.reduce((sum, field) => {
        return sum + parseInt(row[field] || 0);
      }, 0);
      row[totalField] = total.toString();

      // Update the form data
      form.setFieldsValue({ [fieldName]: currentData });

      // Calculate the column-wise totals for the last row
      calculateColumnTotals(
        form,
        fieldName,
        fieldsToSum,
        totalField,
        totalRowIndex,
      );
    }
  };

  // Q9 ---------------------------Q30-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------

  const DATA_SOURCE_SCHOOL_PT1_Q9: DATA_TYPE_SCHOOL_PT1_Q9[] = [
    {
      key: "1",
      label: "RIGVEDA",
      ved: Text({ tid: "STUDENT_LEVEL_RIGVED", def: "" }),
      vedShakha1: Text({ tid: "SHAKAL", def: "SHAKAL" }),
      vedShakha2: Text({ tid: "BASHKAL", def: "BASHKAL" }),
      vedShakha3: Text({ tid: "SHANKAYAN", def: "SHANKAYAN" }),
      vedShakha4: Text({ tid: "ANYA", def: "ANYA" }),
    },
    {
      key: "2",
      label: "SHUKLA YAJURVEDA",
      ved: Text({
        tid: "STUDENT_LEVEL_SUKLA_YAJURVED",
        def: "SUKLA_YAJURVED",
      }),
      vedShakha1: Text({ tid: "KANVA", def: "KANVA" }),
      vedShakha2: Text({ tid: "MADHYADINA", def: "MADHYADINA" }),
      vedShakha3: Text({ tid: "ANYA", def: "ANYA" }),
      vedShakha4: "",
    },
    {
      key: "3",
      label: "KRISHNA YAJURVEDA",
      ved: Text({
        tid: "STUDENT_LEVEL_KRISHNA_YAJURVED",
        def: "KRISHNA_YAJURVED",
      }),
      vedShakha1: Text({ tid: "TAITTIRIYA", def: "TAITTIRIYA" }),
      vedShakha2: Text({ tid: "MAITRAYANI", def: "MAITRAYANI" }),
      vedShakha3: Text({ tid: "ANYA", def: "ANYA" }),
      vedShakha4: "",
    },
    {
      key: "4",
      label: "SAMAVEDA",
      ved: Text({ tid: "STUDENT_LEVEL_SAMAVED", def: "SAMAVED" }),
      vedShakha1: Text({ tid: "KAUTHUMA", def: "KAUTHUMA" }),
      vedShakha2: Text({ tid: "JAIMINIYA", def: "JAIMINIYA" }),
      vedShakha3: Text({ tid: "RANAYANI", def: "RANAYANI" }),
      vedShakha4: Text({ tid: "ANYA", def: "ANYA" }),
    },
    {
      key: "5",
      label: "ATHARVAVEDA",
      ved: Text({ tid: "STUDENT_LEVEL_ATHARVAVED", def: "ATHARVAVED" }),
      vedShakha1: Text({ tid: "SHAUNAKA", def: "SHAUNAKA" }),
      vedShakha2: Text({ tid: "PIPPALADA", def: "PIPPALADA" }),
      vedShakha3: Text({ tid: "SHAKAL", def: "SHAKAL" }),
      vedShakha4: Text({ tid: "ANYA", def: "ANYA" }),
    },
  ];
  const TABLE_COLUMNS_SCHOOL_PT1_Q9: TableColumnsType<DATA_TYPE_SCHOOL_PT1_Q9> =
    [
      {
        dataIndex: "ved",
        render: (text) => <span className="bold-text">{text}</span>,
        width: 150,
      },
      ...DATA_SOURCE_SCHOOL_PT1_Q9.reduce(
        (columns: TableColumnsType<DATA_TYPE_SCHOOL_PT1_Q9>, row) => {
          // Get all vedShakha keys that exist in the current row
          const shakhaKeys = Object.keys(row).filter(
            (key) =>
              key.startsWith("vedShakha") &&
              row[key as keyof DATA_TYPE_SCHOOL_PT1_Q9], // Only include keys with non-empty values
          );

          // Add columns for vedShakha keys that haven't been added yet
          shakhaKeys.forEach((shakhaKey) => {
            if (
              !columns.some(
                (col) => "dataIndex" in col && col.dataIndex === shakhaKey,
              )
            ) {
              columns.push({
                dataIndex: shakhaKey,
                width: 200,
                render: (text: string, record: DATA_TYPE_SCHOOL_PT1_Q9) => {
                  const isOther = text === OTHER;
                  // Get vedIndex from record.key (key is "1"-"5", so index is 0-4)
                  const vedIndex = parseInt(record.key) - 1;

                  // Get form data using array-based access
                  const formData: FORM_DATA_SCHOOL_PT1_Q9[] =
                    form.getFieldValue("SCHOOL_2026_PT1_Q9") ||
                    INITIAL_SCHOOL_PT1_Q9_DATA;

                  const vedData = formData[vedIndex] || {
                    vedIndex,
                    selectedShakhas: [],
                    anyaDetails: "",
                  };

                  const isChecked =
                    Array.isArray(vedData.selectedShakhas) &&
                    vedData.selectedShakhas.includes(text);

                  return text ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Form.Item valuePropName="checked">
                        <Checkbox
                          disabled={disabled || isReviewMode}
                          checked={isChecked}
                          onChange={(e) => {
                            onCheckboxChange(vedIndex, text, e.target.checked);
                          }}
                        >
                          {text}
                        </Checkbox>
                      </Form.Item>
                      {isOther && isChecked && (
                        <Form.Item
                          style={{ marginBottom: 0 }}
                          name={["SCHOOL_2026_PT1_Q9", vedIndex, "anyaDetails"]}
                          rules={[
                            {
                              required: true,
                              message: Text({
                                tid: "REQUIRED",
                                def: "Required",
                              }),
                            },
                          ]}
                        >
                          <Input
                            placeholder=""
                            style={{ minWidth: "100px" }}
                            disabled={disabled || isReviewMode}
                          />
                        </Form.Item>
                      )}
                    </div>
                  ) : null;
                },
              });
            }
          });

          return columns;
        },
        [] as TableColumnsType<DATA_TYPE_SCHOOL_PT1_Q9>, // Initialize columns as an empty array
      ),
    ];

  // Q10 ---------------------------Q10-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q10RowCount = 13;

  const DATA_TYPE_SCHOOL_2026_PT1_Q10_label = [
    Text({ tid: "RIGVEDA_SHAKAL", def: "RIGVEDA_SHAKAL" }),
    Text({ tid: "RIGVEDA_BASKAL", def: "RIGVEDA_BASKAL" }),
    Text({ tid: "RIGVEDA_SHANKHAYAN", def: "RIGVEDA_SHANKHAYAN" }),
    Text({
      tid: "SHUKLA_YAJURVEDA_KANVA",
      def: "SHUKLA_YAJURVEDA_KANVA",
    }),
    Text({
      tid: "SHUKLA_YAJURVEDA_MADHYANDINA",
      def: "SHUKLA_YAJURVEDA_MADHYANDINA",
    }),
    Text({
      tid: "KRISHNA_YAJURVEDA_TAITTIRIYA",
      def: "KRISHNA_YAJURVEDA_TAITTIRIYA",
    }),
    Text({
      tid: "KRISHNA_YAJURVEDA_MAITRAYANI",
      def: "KRISHNA_YAJURVEDA_MAITRAYANI",
    }),
    Text({ tid: "SAMAVEDA_KAUTHUM", def: "SAMAVEDA_KAUTHUM" }),
    Text({ tid: "SAMAVEDA_JAIMINIYA", def: "SAMAVEDA_JAIMINIYA" }),
    Text({ tid: "SAMAVEDA_RANAYANIYA", def: "SAMAVEDA_RANAYANIYA" }),
    Text({ tid: "ATHARVAVEDA_SHAUNAKA", def: "ATHARVAVEDA_SHAUNAKA" }),
    Text({
      tid: "ATHARVAVEDA_PAIPPALADA",
      def: "ATHARVAVEDA_PAIPPALADA",
    }),
    Text({ tid: "TOTAL_SUM", def: "TOTAL_SUM" }),
  ];

  const TABLE_COLUMNS_SCHOOL_PT1_Q10: TableProps<DATA_TYPE_SCHOOL_PT1_Q10>["columns"] =
    [
      {
        title: Text({ tid: "VEDASHAKHA", def: "VEDASHAKHA" }),
        dataIndex: "vedShakha",
        key: "vedShakha",
        render: (_, record, index) => {
          const labels = [
            Text({ tid: "RIGVEDA_SHAKAL", def: "RIGVEDA_SHAKAL" }),
            Text({ tid: "RIGVEDA_BASKAL", def: "RIGVEDA_BASKAL" }),
            Text({ tid: "RIGVEDA_SHANKHAYAN", def: "RIGVEDA_SHANKHAYAN" }),
            Text({
              tid: "SHUKLA_YAJURVEDA_KANVA",
              def: "SHUKLA_YAJURVEDA_KANVA",
            }),
            Text({
              tid: "SHUKLA_YAJURVEDA_MADHYANDINA",
              def: "SHUKLA_YAJURVEDA_MADHYANDINA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_TAITTIRIYA",
              def: "KRISHNA_YAJURVEDA_TAITTIRIYA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_MAITRAYANI",
              def: "KRISHNA_YAJURVEDA_MAITRAYANI",
            }),
            Text({ tid: "SAMAVEDA_KAUTHUM", def: "SAMAVEDA_KAUTHUM" }),
            Text({ tid: "SAMAVEDA_JAIMINIYA", def: "SAMAVEDA_JAIMINIYA" }),
            Text({ tid: "SAMAVEDA_RANAYANIYA", def: "SAMAVEDA_RANAYANIYA" }),
            Text({ tid: "ATHARVAVEDA_SHAUNAKA", def: "ATHARVAVEDA_SHAUNAKA" }),
            Text({
              tid: "ATHARVAVEDA_PAIPPALADA",
              def: "ATHARVAVEDA_PAIPPALADA",
            }),
            Text({ tid: "TOTAL_SUM", def: "TOTAL_SUM" }),
          ];
          return (
            labels[index] ||
            Text({ tid: "DEFAULT_LEVEL", def: "DEFAULT LEVEL" })
          );
        },
      },
      {
        title: Text({ tid: "FULL_TIME_TEACHER", def: "FULL_TIME_TEACHER" }),
        dataIndex: "fullTimeTeacher",
        key: "fullTimeTeacher",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q10", index, "fullTimeTeacher"]}
            rules={
              index === schoolPt1Q10RowCount - 1
                ? [] // No validation rules for TOTAL_SUM row
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={disabled || index === schoolPt1Q10RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q10",
                  index,
                  "fullTimeTeacher",
                  value,
                  ["fullTimeTeacher", "partTimeTeacher"],
                  "totalTeacher",
                  schoolPt1Q10RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q10",
                  form.getFieldValue("SCHOOL_2026_PT1_Q10"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "PART_TIME_TEACHER", def: "PART_TIME_TEACHER" }),
        dataIndex: "partTimeTeacher",
        key: "partTimeTeacher",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q10", index, "partTimeTeacher"]}
            rules={
              index === schoolPt1Q10RowCount - 1
                ? [] // No validation rules for TOTAL_SUM row
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled || index === schoolPt1Q10RowCount - 1 || isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q10",
                  index,
                  "partTimeTeacher",
                  value,
                  ["fullTimeTeacher", "partTimeTeacher"],
                  "totalTeacher",
                  schoolPt1Q10RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q10",
                  form.getFieldValue("SCHOOL_2026_PT1_Q10"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TOTAL_TEACHER", def: "TOTAL_TEACHER" }),
        dataIndex: "totalTeacher",
        key: "totalTeacher",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q10", index, "totalTeacher"]}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input type="number" disabled value={record.totalTeacher || ""} />
          </Form.Item>
        ),
      },
    ];
  const DATA_SOURCE_SCHOOL_PT1_Q10: DATA_TYPE_SCHOOL_PT1_Q10[] = Array.from(
    { length: schoolPt1Q10RowCount },
    (_, index) => ({
      vedShakha: DATA_TYPE_SCHOOL_2026_PT1_Q10_label[index],
      fullTimeTeacher: 0,
      partTimeTeacher: 0,
      totalTeacher: 0,
    }),
  );

  // Q12_A ---------------------------Q12_A-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q12_A_RowCount = 13;
  const TABLE_COLUMNS_SCHOOL_PT1_Q12_A: TableProps<DATA_TYPE_SCHOOL_PT1_Q12_A>["columns"] =
    [
      {
        title: Text({ tid: "VEDASHAKHA", def: "VEDASHAKHA" }),
        dataIndex: "vedShakha",
        key: "vedShakha",
        render: (_, record, index) => {
          const texts = [
            Text({ tid: "RIGVEDA_SHAKAL", def: "RIGVEDA_SHAKAL" }),
            Text({ tid: "RIGVEDA_BASKAL", def: "RIGVEDA_BASKAL" }),
            Text({ tid: "RIGVEDA_SHANKHAYAN", def: "RIGVEDA_SHANKHAYAN" }),
            Text({
              tid: "SHUKLA_YAJURVEDA_KANVA",
              def: "SHUKLA_YAJURVEDA_KANVA",
            }),
            Text({
              tid: "SHUKLA_YAJURVEDA_MADHYANDINA",
              def: "SHUKLA_YAJURVEDA_MADHYANDINA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_TAITTIRIYA",
              def: "KRISHNA_YAJURVEDA_TAITTIRIYA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_MAITRAYANI",
              def: "KRISHNA_YAJURVEDA_MAITRAYANI",
            }),
            Text({ tid: "SAMAVEDA_KAUTHUM", def: "SAMAVEDA_KAUTHUM" }),
            Text({ tid: "SAMAVEDA_JAIMINIYA", def: "SAMAVEDA_JAIMINIYA" }),
            Text({ tid: "SAMAVEDA_RANAYANIYA", def: "SAMAVEDA_RANAYANIYA" }),
            Text({ tid: "ATHARVAVEDA_SHAUNAKA", def: "ATHARVAVEDA_SHAUNAKA" }),
            Text({
              tid: "ATHARVAVEDA_PAIPPALADA",
              def: "ATHARVAVEDA_PAIPPALADA",
            }),
            Text({ tid: "TOTAL_SUM", def: "TOTAL_SUM" }),
          ];

          return (
            texts[index] || Text({ tid: "DEFAULT_LEVEL", def: "DEFAULT LEVEL" })
          );
        },
      },
      {
        title: Text({ tid: "MOOLANT_STUDENTS", def: "MOOLANT_STUDENTS" }),
        dataIndex: "moolantStudents",
        key: "moolantStudents",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_A", index, "moolantStudents"]}
            rules={
              index === schoolPt1Q12_A_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_A_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_A",
                  index,
                  "moolantStudents",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q12_A_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_A",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_A"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_1_STUDENTS", def: "LEVEL_1_STUDENTS" }),
        dataIndex: "level1Students",
        key: "level1Students",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_A", index, "level1Students"]}
            rules={
              index === schoolPt1Q12_A_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_A_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_A",
                  index,
                  "level1Students",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q12_A_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_A",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_A"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_2_STUDENTS", def: "LEVEL_2_STUDENTS" }),
        dataIndex: "level2Students",
        key: "level2Students",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_A", index, "level2Students"]}
            rules={
              index === schoolPt1Q12_A_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_A_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_A",
                  index,
                  "level2Students",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q12_A_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_A",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_A"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "BEYOND_LEVEL_2_STUDENTS",
          def: "BEYOND_LEVEL_2_STUDENTS",
        }),
        dataIndex: "beyondLevel2Students",
        key: "beyondLevel2Students",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_A", index, "beyondLevel2Students"]}
            rules={
              index === schoolPt1Q12_A_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_A_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_A",
                  index,
                  "beyondLevel2Students",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q12_A_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_A",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_A"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TOTAL_VED_STUDENTS", def: "TOTAL_VED_STUDENTS" }),
        dataIndex: "totalVedStudents",
        key: "totalVedStudents",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_A", index, "totalVedStudents"]}
          >
            <Input
              type="number"
              disabled
              value={record.totalVedStudents || ""}
            />
          </Form.Item>
        ),
      },
    ];
  const DATA_SOURCE_SCHOOL_PT1_Q12_A: DATA_TYPE_SCHOOL_PT1_Q12_A[] = Array.from(
    { length: schoolPt1Q12_A_RowCount },
    (_, index) => ({
      vedShakha: index === schoolPt1Q12_A_RowCount - 1 ? "TOTAL_SUM" : "",
      moolantStudents: 0,
      level1Students: 0,
      level2Students: 0,
      beyondLevel2Students: 0,
      totalVedStudents: 0,
    }),
  );

  // Q12_B ---------------------------Q12_B-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q12_B_RowCount = 5;

  const TABLE_COLUMNS_SCHOOL_PT1_Q12_B: TableProps<DATA_TYPE_SCHOOL_PT1_Q12_B>["columns"] =
    [
      {
        title: Text({
          tid: "YEAR_ACADEMIC_SESSION",
          def: "YEAR_ACADEMIC_SESSION",
        }),
        dataIndex: "year",
        width: 100,
        key: "year",
      },
      {
        title: Text({
          tid: "STUDENTS_START_SESSION",
          def: "STUDENTS_START_SESSION",
        }),
        dataIndex: "studentStartSession",
        key: "studentStartSession",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_B", index, "studentStartSession"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input type="number" disabled={disabled || isReviewMode} />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "NEW_VEDIC_STUDENTS", def: "NEW_VEDIC_STUDENTS" }),
        dataIndex: "newVedicStudent",
        key: "newVedicStudent",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_B", index, "newVedicStudent"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input type="number" disabled={disabled || isReviewMode} />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "STUDENTS_COMPLETED_STUDY",
          def: "STUDENTS_COMPLETED_STUDY",
        }),
        dataIndex: "studentCompletedStudy",
        key: "studentCompletedStudy",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_B", index, "studentCompletedStudy"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input type="number" disabled={disabled || isReviewMode} />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "STUDENTS_DROPPED_OUT",
          def: "STUDENTS_DROPPED_OUT",
        }),
        dataIndex: "studentDroppedOut",
        key: "studentDroppedOut",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_B", index, "studentDroppedOut"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input type="number" disabled={disabled || isReviewMode} />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "STUDENTS_END_SESSION",
          def: "STUDENTS_END_SESSION",
        }),
        dataIndex: "studentEndSession",
        key: "studentEndSession",
        render: (_, record, index) => (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const start =
                getFieldValue([
                  "SCHOOL_2026_PT1_Q12_B",
                  index,
                  "studentStartSession",
                ]) || 0;

              const newVedic =
                getFieldValue([
                  "SCHOOL_2026_PT1_Q12_B",
                  index,
                  "newVedicStudent",
                ]) || 0;

              const completed =
                getFieldValue([
                  "SCHOOL_2026_PT1_Q12_B",
                  index,
                  "studentCompletedStudy",
                ]) || 0;

              const dropped =
                getFieldValue([
                  "SCHOOL_2026_PT1_Q12_B",
                  index,
                  "studentDroppedOut",
                ]) || 0;

              const endSession =
                Number(start) +
                Number(newVedic) -
                Number(completed) -
                Number(dropped);

              return <Input value={endSession} disabled />;
            }}
          </Form.Item>
        ),
      },
    ];

  // Initial data source
  const DATA_SOURCE_SCHOOL_PT1_Q12_B: DATA_TYPE_SCHOOL_PT1_Q12_B[] = Array.from(
    { length: schoolPt1Q12_B_RowCount },
    (_, index) => ({
      year: `${getNumberInLanguage(Number(new Date().getFullYear() - index - 1))} -${getNumberInLanguage(Number(26 - index))}`,
      studentStartSession: 0,
      studentCompletedStudy: 0,
      newVedicStudent: 0,
      studentDroppedOut: 0,
      studentEndSession: 0,
    }),
  );

  // Q12-C ---------------------------Q12-C-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q12_C_RowCount = 6;

  const TABLE_COLUMNS_SCHOOL_PT1_Q12_C: TableProps<DATA_TYPE_SCHOOL_PT1_Q12_C>["columns"] =
    [
      {
        title: Text({ tid: "VED", def: "VED" }),
        dataIndex: "Ved",
        key: "Ved",
        render: (_, record, index) => {
          const texts = [
            Text({ tid: "STUDENT_LEVEL_RIGVED", def: "SCHOOL_LEVEL_RIGVED" }),
            Text({
              tid: "STUDENT_LEVEL_SUKLA_YAJURVED",
              def: "SCHOOL_LEVEL_SUKLA_YAJURVED",
            }),
            Text({
              tid: "STUDENT_LEVEL_KRISHNA_YAJURVED",
              def: "SCHOOL_LEVEL_KRISHNA_YAJURVED",
            }),
            Text({
              tid: "STUDENT_LEVEL_SAMAVED",
              def: "SCHOOL_LEVEL_SAMAVED",
            }),
            Text({
              tid: "STUDENT_LEVEL_ATHARVAVED",
              def: "SCHOOL_LEVEL_ATHARVAVED",
            }),
            Text({ tid: "TOTAL_JOINED", def: "TOTAL_JOINED" }),
          ];
          return (
            texts[index] || Text({ tid: "DEFAULT_VED", def: "DEFAULT VED" })
          );
        },
      },
      {
        title: Text({ tid: "MOOLPATI", def: "MOOLPATI" }),
        dataIndex: "moolpathi",
        key: "moolpathi",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_C", index, "moolpathi"]}
            rules={
              index === schoolPt1Q12_C_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_C_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_C",
                  index,
                  "moolpathi",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q12_C_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_C",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_C"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_1", def: "LEVEL_1" }),
        dataIndex: "level1",
        key: "level1",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_C", index, "level1"]}
            rules={
              index === schoolPt1Q12_C_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_C_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_C",
                  index,
                  "level1",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q12_C_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_C",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_C"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_2", def: "LEVEL_2" }),
        dataIndex: "level2",
        key: "level2",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_C", index, "level2"]}
            rules={
              index === schoolPt1Q12_C_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_C_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_C",
                  index,
                  "level2",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q12_C_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_C",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_C"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_BEYOND_2", def: "LEVEL_BEYOND_2" }),
        dataIndex: "beyondLevel2",
        key: "beyondLevel2",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_2026_PT1_Q12_C", index, "beyondLevel2"]}
            rules={
              index === schoolPt1Q12_C_RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type="number"
              disabled={
                disabled ||
                index === schoolPt1Q12_C_RowCount - 1 ||
                isReviewMode
              }
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_2026_PT1_Q12_C",
                  index,
                  "beyondLevel2",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q12_C_RowCount - 1,
                );
                debouncedSave(
                  "SCHOOL_2026_PT1_Q12_C",
                  form.getFieldValue("SCHOOL_2026_PT1_Q12_C"),
                  eAnswerType.ARRAY,
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TOTAL_JOINED", def: "TOTAL_JOINED" }),
        dataIndex: "total",
        key: "total",
        render: (_, record, index) => (
          <Form.Item name={["SCHOOL_2026_PT1_Q12_C", index, "total"]}>
            <Input type="number" disabled value={record.total || ""} />
          </Form.Item>
        ),
      },
    ];

  const DATA_SOURCE_SCHOOL_PT1_Q12_C: DATA_TYPE_SCHOOL_PT1_Q12_C[] = Array.from(
    { length: schoolPt1Q12_C_RowCount },
    (_, index) => ({
      level: index === schoolPt1Q12_C_RowCount - 1 ? "TOTAL_JOINED" : "",
      moolpathi: 0,
      level1: 0,
      level2: 0,
      beyondLevel2: 0,
      total: 0,
    }),
  );
  // -------------------------------------------------------------------------------------------

  const LengthCounter: React.FC<{ form: any; fieldName: string }> = ({
    form,
    fieldName,
  }) => {
    // Use useWatch to get real-time updates for the field value
    const fieldValue = Form.useWatch(fieldName, form);

    return (
      <div className="length_counter">
        {(fieldValue || "").length}/{INPUT_LENGTH.TEXTAREA_LENGTH}
      </div>
    );
  };

  return (
    <div
      id="schoolFormPart1"
      className={`${styles.formWrapper} studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name="register"
        onFinish={handleFormSubmit}
        onValuesChange={handleLocalFormChange}
        initialValues={{}}
        scrollToFirstError
        layout="vertical"
      >
        {/* FORM HEADING */}
        <>
          <Row>
            <Typography.Title level={3}>
              {Text({
                tid: "SCHOOL_2026_PART_1_FORM_HEADING",
                def: "School part-1",
              })}
            </Typography.Title>
            {!disabled && (
              <Typography.Text type="danger">
                {Text({
                  tid: "all_fields_are_mandatory",
                  def: "All fields are mandatory!",
                })}
              </Typography.Text>
            )}
          </Row>
          {isReviewMode && (
            <Row style={{ marginBottom: 10 }}>
              <Typography.Text type="warning">
                <CheckCircleOutlined style={{ marginRight: 6 }} />
                {Text({
                  tid: "REVIEW_MODE_INSTRUCTION",
                  def: "You are in review mode. You can only view the submitted data. To make changes, please switch to edit mode.",
                })}
              </Typography.Text>
            </Row>
          )}
          <Row>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT1_HEADING_Q1",
                def: "Name and full address of the Ved school:",
              })}
            </Typography.Text>
          </Row>
        </>
        {/* 1. Name of Vedvidhyalaya and full address */}
        <>
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q1_A"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q1_A",
                  def: "Name of Vedvidhyalaya",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q1_A_PLACEHOLDER",
                    def: "Ex. Sanatan Vedic Shikshan Sansthan",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={12}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q1_B"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q1_B",
                  def: "Full address of Vedvidhyalaya",
                })}
                rules={inputRequiredBasicRule}
              >
                <>
                  <Input.TextArea
                    disabled={disabled || isReviewMode}
                    placeholder={Text({
                      tid: "SCHOOL_2026_PT1_Q1_B_PLACEHOLDER",
                      def: "Ex. Village- Kasar, Charbhuja, District- Rajsamand, Rajasthan, India, Pin - 313333",
                    })}
                    maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                    style={{ paddingBottom: 20 }}
                  />
                </>
              </Form.Item>
              <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q1_B" />
            </Col>
          </Row>
        </>
        {/* 2. Question 2*/}
        <>
          <Row>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT1_HEADING_Q2",
                def: "Information about Head of the Institution",
              })}
            </Typography.Text>
          </Row>
          {/* 2.1 Name */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q2_A"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q2_A",
                  def: "Name",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q2_A_PLACEHOLDER",
                    def: "Ex. Pt. Umesh Dwivedi",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 2.2. Full address */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={12}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q2_B"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q2_B",
                  def: "Full address",
                })}
                rules={inputRequiredBasicRule}
              >
                <>
                  <Input.TextArea
                    disabled={disabled || isReviewMode}
                    placeholder={Text({
                      tid: "SCHOOL_2026_PT1_Q2_B_PLACEHOLDER",
                      def: "Ex. Village- Kasar, Charbhuja, District- Rajsamand, Rajasthan, India, Pin - 313333",
                    })}
                    maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                    style={{ paddingBottom: 20 }}
                  />
                </>
              </Form.Item>
              <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q2_B" />
            </Col>
          </Row>
          {/* 2.3. Mobile No. */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q2_C"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q2_C",
                  def: "Mobile No.",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                  {
                    pattern: Regex.MOBILENO, // Updated pattern
                    message: Text({
                      tid: "mobile_number_validation",
                      def: "Enter a valid 10-digit mobile number",
                    }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q2_C_PLACEHOLDER",
                    def: "Ex. 7357658777",
                  })}
                  maxLength={10}
                  onKeyDown={allowOnlyNumericInput_onKeyDown}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 2.4. Email ID */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q2_D"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q2_D",
                  def: "Email ID",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },

                  {
                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: Text({
                      tid: "email_validation",
                      def: "Enter a valid email address",
                    }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q2_D_PLACEHOLDER",
                    def: "Ex. applications@bharatatmapuraskar.org",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
        {/* 3. Website */}
        <Row>
          <Col xs={24} lg={12} xl={8}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q3"
              label={Text({
                tid: "SCHOOL_2026_PT1_Q3",
                def: "Website",
              })}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "SCHOOL_2026_PT1_Q3_PLACEHOLDER",
                  def: "Ex. www.bharatatmapuraskar.org",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 4. Question 4*/}
        <>
          <Row>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT1_Q4_HEADING",
                def: "",
              })}
            </Typography.Text>
          </Row>
          {/* 4.1 Name */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q4_A"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q4_A",
                  def: "Name",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q4_A_PLACEHOLDER",
                    def: "Ex. Yogesh Paliwal",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 4.2 Full address */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q4_B"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q4_B",
                  def: "Full address",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q4_B_PLACEHOLDER",
                    def: "Ex. House No.- 01, Pratapnagar Industrial Area, Near Pratapnagar Circle, Udaipur, Rajasthan, India, Pin-313001",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 9. Mobile No. */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q4_C"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q4_C",
                  def: "Mobile No.",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                  {
                    pattern: Regex.MOBILENO, // Updated pattern
                    message: Text({
                      tid: "mobile_number_validation",
                      def: "Enter a valid 10-digit mobile number",
                    }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q4_C_PLACEHOLDER",
                    def: "Ex. 7357658777",
                  })}
                  maxLength={10}
                  onKeyDown={allowOnlyNumericInput_onKeyDown}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 10. Email ID */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q4_D"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q4_D",
                  def: "Email ID",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                  {
                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: Text({
                      tid: "VALID_EMAIL",
                      def: "VALID_EMAIL",
                    }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q4_D_PLACEHOLDER",
                    def: "Ex. applications@bharatatmapuraskar.org",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
        {/* 5. Language */}
        <Row>
          <Col xs={24} lg={24} xl={24}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q5"
              label={Text({
                tid: "SCHOOL_2026_PT1_Q5",
                def: "In which language should the relevant conversation be conducted with you?",
              })}
              rules={inputRequiredBasicRule}
            >
              <Radio.Group disabled={disabled || isReviewMode}>
                <Radio value="English">
                  {Text({ tid: "ENGLISH", def: "English" })}
                </Radio>
                <Radio value="हिन्दी">
                  {Text({ tid: "HINDI", def: "Hindi" })}
                </Radio>
                <Radio value="संस्कृतम्">
                  {Text({ tid: "SANSKRIT", def: "Sanskrit" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {/*Qestion 6*/}
        <>
          {/* Heading */}
          <Row>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT1_Q6_HEADING",
                def: "6. Details related to founders",
              })}
            </Typography.Text>
          </Row>

          {/* 6.1 Select organization type */}
          <Row className="QuestionsSubSection">
            <Col xs={24}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q6_A"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q6_A",
                  def: "Your institution is:",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Radio.Group disabled={disabled || isReviewMode}>
                  <Radio value="INSTITUTION_TRUST">
                    {Text({
                      tid: "INSTITUTION_TRUST",
                      def: "Trust (न्यास)",
                    })}
                  </Radio>
                  <Radio value="INSTITUTION_OTHER">
                    {Text({
                      tid: "INSTITUTION_OTHER",
                      def: "Other Institution (अन्य संस्था)",
                    })}
                  </Radio>
                  <Radio value="INDIVIDUAL">
                    {Text({
                      tid: "INSTITUTION_INDIVIDUAL",
                      def: "Individual Institution (व्यक्तिगत संस्था)",
                    })}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          {/* 6.2 Name */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q6_B"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q6_B",
                  def: "Name of the trust / institution / sponsor",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q6_B_PLACEHOLDER",
                    def: "Ex. Singhal Foundation",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 6.3 Registration No */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q6_C"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q6_C",
                  def: "Registration number (if any)",
                })}
                rules={[
                  {
                    required: false,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q6_C_PLACEHOLDER",
                    def: "Ex. 78/Udaipur/2019-20",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 6.4 Chief Trustee Name */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q6_D"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q6_D",
                  def: "Name of the chief trustee / founder",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q6_D_PLACEHOLDER",
                    def: "Ex. Pt. Umesh Dwivedi",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 6.5 Full Address */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={12}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q6_E"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q6_E",
                  def: "Full address",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q6_E_PLACEHOLDER",
                    def: "Ex. House No.- 01, Pratapnagar Industrial Area, Near Pratapnagar Circle, Udaipur, Rajasthan, India, Pin-313001",
                  })}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </Form.Item>
              <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q6_E" />
            </Col>
          </Row>

          {/* 6.6 Mobile */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q6_F"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q6_F",
                  def: "Mobile number",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                  {
                    pattern: Regex.MOBILENO,
                    message: Text({
                      tid: "mobile_number_validation",
                      def: "Enter a valid 10-digit mobile number",
                    }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q6_F_PLACEHOLDER",
                    def: "Ex. 7357658777",
                  })}
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 6.7 Email */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q6_G"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q6_G",
                  def: "Email address",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                  {
                    type: "email",
                    message: Text({
                      tid: "INVALID_EMAIL",
                      def: "Invalid email address",
                    }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q6_G_PLACEHOLDER",
                    def: "Ex. applications@bharatatmapuraskar.org",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        </>

        {/* 7. Date of commencement of teaching in the Ved school  */}
        <Row>
          <Col xs={24} lg={12} xl={8}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q7"
              label={Text({
                tid: "SCHOOL_2026_PT1_Q7",
                def: "Date of commencement of teaching in the Ved school",
              })}
              rules={inputRequiredBasicRule}
            >
              <DatePicker
                format="DD-MM-YYYY"
                placeholder={Text({
                  tid: "SCHOOL_2026_PT1_Q7_PLACEHOLDER",
                  def: "Ex. 21-05-2001",
                })}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                disabled={disabled || isReviewMode}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 8. Has the Ved school ever been completely closed for any period? If yes, from when to when? */}
        <>
          <Row>
            <Col xs={24} lg={24} xl={24}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q8"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q8",
                  def: "Has the Ved school ever been completely closed for any period? If yes, from when to when?",
                })}
                rules={inputRequiredBasicRule}
              >
                <Radio.Group disabled={disabled || isReviewMode}>
                  <Radio value={eYES_OR_NO.YES}>
                    {Text({ tid: "YES", def: "Yes" })}
                  </Radio>
                  <Radio value={eYES_OR_NO.NO}>
                    {Text({ tid: "NO", def: "No" })}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          {/*  starting date and ending date */}
          {form.getFieldValue("SCHOOL_2026_PT1_Q8") === eYES_OR_NO.YES && (
            <Row gutter={30}>
              <Col sm={12} lg={6} xl={4}>
                <Form.Item
                  className="w-full"
                  name="SCHOOL_2026_PT1_Q8_A"
                  label={Text({
                    tid: "SCHOOL_2026_PT1_Q8_A",
                    def: "From",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <DatePicker
                    picker="month"
                    format="MMM-YYYY"
                    placeholder={Text({
                      tid: "SELECT_DATE",
                      def: "Ex. June-2019",
                    })}
                    disabled={disabled || isReviewMode}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col sm={12} lg={6} xl={4}>
                <Form.Item
                  className="w-full"
                  name="SCHOOL_2026_PT1_Q8_B"
                  label={Text({
                    tid: "SCHOOL_2026_PT1_Q8_B",
                    def: "To",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <DatePicker
                    picker="month"
                    format="MMM-YYYY"
                    placeholder={Text({
                      tid: "SELECT_DATE",
                      def: "Ex. May-2020",
                    })}
                    disabled={disabled || isReviewMode}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </>
        {/* 9. QUESTION TABLE */}
        <>
          <Row>
            <Col xs={24}>
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_2026_PT1_Q9",
                  def: "Which Vedas and branches of the Ved are currently being taught? Please tick ✓",
                })}
                {/* <FormHelpTooltip
                  title={Text({
                    tid: "SCHOOL_2026_PT1_Q9_TOOLTIP",
                    def: "Select all the Veda branches that are currently taught in your institution.",
                  })}
                /> */}
              </Typography.Text>
            </Col>
          </Row>
          {/* 9. INPUT TABLE */}
          <Row>
            <Col xs={24}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q9"
                className="checkboxTable"
                rules={[
                  {
                    validator: async (_, value: FORM_DATA_SCHOOL_PT1_Q9[]) => {
                      if (!value) {
                        return Promise.reject(SELECT_AT_LEAST_ONE_MSG);
                      }

                      const hasSelected = value.some(
                        (row) =>
                          row.selectedShakhas && row.selectedShakhas.length > 0,
                      );
                      if (!hasSelected) {
                        return Promise.reject(SELECT_AT_LEAST_ONE_MSG);
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Table<DATA_TYPE_SCHOOL_PT1_Q9>
                  columns={TABLE_COLUMNS_SCHOOL_PT1_Q9}
                  scroll={{ x: "80vw" }}
                  dataSource={DATA_SOURCE_SCHOOL_PT1_Q9}
                  showHeader={false}
                  pagination={false}
                  // style={{ width: "100%" }}
                  size="large"
                  id="school-pt1-Q9-table"
                />
              </Form.Item>
            </Col>
          </Row>
        </>

        {/* 10. QUESTION HEADING */}
        <>
          <Row>
            <Col xs={24}>
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_2026_PT1_Q10",
                  def: "Current number of full-time / part-time teachers (if a teacher teaches more than one branch of Ved, count them under their primary branch)",
                })}
                {/* <FormHelpTooltip
                  title={Text({
                    tid: "SCHOOL_2026_PT1_Q10_TOOLTIP",
                    def: "Please provide details of full-time and part-time teachers.",
                  })}
                /> */}
              </Typography.Text>
            </Col>
          </Row>

          {/* 10. INPUT TABLE */}
          <Row>
            <Col xs={24}>
              <Form.Item name="SCHOOL_2026_PT1_Q10">
                <Table<DATA_TYPE_SCHOOL_PT1_Q10>
                  columns={TABLE_COLUMNS_SCHOOL_PT1_Q10}
                  scroll={{ x: "80vw" }}
                  dataSource={DATA_SOURCE_SCHOOL_PT1_Q10}
                  pagination={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </>

        {/* 11. QUESTION HEADING*/}
        <Row>
          <Col xs={24}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q11"
              label={
                <>
                  {Text({
                    tid: "SCHOOL_2026_PT1_Q11",
                    def: "11. Effort made by teachers for studying / teaching rare branches of the Ved or advanced Ved knowledge  :",
                  })}
                  {/* <FormHelpTooltip
                    title={Text({
                      tid: "SCHOOL_2026_PT1_Q11_TOOLTIP",
                      def: "Describe efforts made for studying/teaching rare Ved branches or advanced Ved knowledge.",
                    })}
                  /> */}
                </>
              }
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q11_PLACEHOLDER",
                    def: "Ex. Fortnightly tests and Shalaka competitions are being organized continuously by our Ved teachers to promote the tradition of studying/teaching Vikriti pathas and Lakshan granthas of the Ved.",
                  })}
                  disabled={disabled || isReviewMode}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_2026_PT1_Q11", e.target.value);
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </>
            </Form.Item>
            <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q11" />
          </Col>
        </Row>

        {/* 12). QUESTION HEADING*/}
        <>
          <Row>
            <Col xs={24}>
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_2026_PT1_Q12_HEADING",
                  def: "12. Information related to students studying the Ved:",
                })}
                {/* <FormHelpTooltip
                  title={Text({
                    tid: "SCHOOL_2026_PT1_Q12_HEADING_TOOLTIP",
                    def: "Provide information related to students studying the Ved.",
                  })}
                /> */}
              </Typography.Text>
            </Col>
          </Row>
          {/* 12.1. INPUT TABLE  */}
          <Row>
            <Col xs={24}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q12_A"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q12_A",
                  def: "Count of Current Students",
                })}
              >
                <Table<DATA_TYPE_SCHOOL_PT1_Q12_A>
                  columns={TABLE_COLUMNS_SCHOOL_PT1_Q12_A}
                  size="large"
                  scroll={{ x: "80vw" }}
                  dataSource={DATA_SOURCE_SCHOOL_PT1_Q12_A}
                  pagination={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 12.2. QUESTION HEADING */}
          <Row>
            <Col xs={24}>
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_2026_PT1_Q12_B",
                  def: "Status of students over the past five years (provide data according to the academic sessions of the Ved school) :",
                })}
              </Typography.Text>
            </Col>
          </Row>

          {/* 12.2. INPUT TABLE */}
          <Row>
            <Col xs={24}>
              <Form.Item name="SCHOOL_2026_PT1_Q12_B">
                <Table<DATA_TYPE_SCHOOL_PT1_Q12_B>
                  columns={TABLE_COLUMNS_SCHOOL_PT1_Q12_B}
                  size="large"
                  scroll={{ x: "80vw" }}
                  dataSource={DATA_SOURCE_SCHOOL_PT1_Q12_B}
                  pagination={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 12.3 QUESTION HEADING */}
          <Row>
            <Col xs={24}>
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_2026_PT1_Q12_C",
                  def: "Number of students who passed exams that are equivalent to those in the list in the guidance section in the past five years (only count students who advanced to a higher level) :",
                })}
              </Typography.Text>
            </Col>
          </Row>
          {/* 12.3 INPUT TABLE */}
          <Row>
            <Col xs={24}>
              <Form.Item name="SCHOOL_2026_PT1_Q12_C">
                <Table<DATA_TYPE_SCHOOL_PT1_Q12_C>
                  columns={TABLE_COLUMNS_SCHOOL_PT1_Q12_C}
                  size="large"
                  scroll={{ x: "80vw" }}
                  dataSource={DATA_SOURCE_SCHOOL_PT1_Q12_C}
                  pagination={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </>

        {/* 13. Purpose of establishing the Ved school */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q13"
              label={
                <>
                  {Text({
                    tid: "SCHOOL_2026_PT1_Q13",
                    def: "Purpose of establishing the Ved school",
                  })}
                  {/* <FormHelpTooltip
                    title={Text({
                      tid: "SCHOOL_2026_PT1_Q13_TOOLTIP",
                      def: "State the purpose of establishing the Ved school.",
                    })}
                  /> */}
                </>
              }
              rules={inputRequiredBasicRule}
              style={{ position: "relative" }} // Relative positioning for the container
            >
              <>
                <Input.TextArea
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q13_PLACEHOLDER",
                    def: "Ex. The main objective of establishing this Ved school is to preserve, promote and propagate the ancient Indian Vedic Shruti-knowledge tradition.",
                  })}
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q13" />
          </Col>
        </Row>

        {/* 14. Any special achievements of the institution in the field of Ved propagation or preservation that make it worthy of this award. */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q14"
              label={
                <>
                  {Text({
                    tid: "SCHOOL_2026_PT1_Q14",
                    def: "Any special achievements of the institution in the field of Ved propagation or preservation that make it worthy of this award.",
                  })}
                  {/* <FormHelpTooltip
                    title={Text({
                      tid: "SCHOOL_2026_PT1_Q14_TOOLTIP",
                      def: "Mention special achievements in Ved propagation or preservation.",
                    })}
                  /> */}
                </>
              }
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q14_PLACEHOLDER",
                    def: "Ex. Teaching work is being done by preserving the endangered Ved branches.",
                  })}
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                />
              </>
            </Form.Item>
            <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q14" />
          </Col>
        </Row>

        {/* 15. Provide details of initiatives undertaken for public awareness regarding the Vedas (Limit your answer to four lines). */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q15"
              label={
                <>
                  {Text({
                    tid: "SCHOOL_2026_PT1_Q15",
                    def: "Provide details of initiatives undertaken for public awareness regarding the Vedas (Limit your answer to four lines).",
                  })}
                  {/* <FormHelpTooltip
                    title={Text({
                      tid: "SCHOOL_2026_PT1_Q15_TOOLTIP",
                      def: "Details of public awareness programs related to Ved.",
                    })}
                  /> */}
                </>
              }
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q15_PLACEHOLDER",
                    def: "Ex. Seminars and lectures are organized in folk languages to make Vedic knowledge useful for the public.",
                  })}
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q15" />
          </Col>
        </Row>

        {/* 16. Mention three achievements of your Ved school that make you proud (Limit your answer to four lines). */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q16"
              label={
                <>
                  {Text({
                    tid: "SCHOOL_2026_PT1_Q16",
                    def: "Mention three achievements of your Ved school that make you proud (Limit your answer to four lines).",
                  })}
                  {/* <FormHelpTooltip
                    title={Text({
                      tid: "SCHOOL_2026_PT1_Q16_TOOLTIP",
                      def: "Mention three achievements of your Ved school that make you proud.",
                    })}
                  /> */}
                </>
              }
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q16_PLACEHOLDER",
                    def: "Ex. 1. The Ved school has prepared more than a hundred Salakshan Ghanant Ved teachers.",
                  })}
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q16" />
          </Col>
        </Row>

        {/* 17. Provide details of Ved-related programs (Parayan, Swahakar, etc.) conducted in the past five years (Limit your answer to four lines). */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name="SCHOOL_2026_PT1_Q17"
              label={
                <>
                  {Text({
                    tid: "SCHOOL_2026_PT1_Q17",
                    def: "Provide details of Ved-related programs (Parayan, Swahakar, etc.) conducted in the past five years (Limit your answer to four lines).",
                  })}
                  {/* <FormHelpTooltip
                    title={Text({
                      tid: "SCHOOL_2026_PT1_Q17_TOOLTIP",
                      def: "Details of Ved related programs conducted in the last 5 years.",
                    })}
                  /> */}
                </>
              }
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q17_PLACEHOLDER",
                    def: "Ex. In the year 2025, the recitation of all texts of Ashtavikriti of Shukla Yajurveda was completed.",
                  })}
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter form={form} fieldName="SCHOOL_2026_PT1_Q17" />
          </Col>
        </Row>
        {/* Affidavit Title */}
        <div id="school_form1_ceo_affidavit" className="affidavitSection">
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_2026_PT1_AFFIDAVIT_HEADING",
                  def: "SCHOOL_PT1_AFFIDAVIT_HEADING",
                })}
              </Typography.Text>
            </Col>
          </Row>
          {/* 40-41. head institute name and years of teaching ved  Affidavit Declaration  */}
          <Row className="affidavitRow">
            <Col xs={24}>
              <Typography.Text type="secondary">
                {Text({
                  tid: "SCHOOL_2026_PT1_AFFIDAVIT_DECLARATION_1",
                  def: "I, (Full name of the Head of the Institution)",
                })}
              </Typography.Text>
              <Form.Item
                name="SCHOOL_2026_PT1_Q18"
                rules={inputRequiredBasicRule}
                className="affidavitRowInput"
              >
                <Input disabled={true} />
              </Form.Item>
              <Typography.Text type="secondary">
                {Text({
                  tid: "SCHOOL_2026_PT1_AFFIDAVIT_DECLARATION_2",
                  def: "solemnly declare that:",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <ul className="mb-10">
            <li>
              <Row className="affidavitRow">
                <Col xs={24}>
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "SCHOOL_2026_PT1_AFFIDAVIT_VED_TEACHING_1",
                      def: "Ved teaching has been continuously conducted in this Ved school for the past",
                    })}
                  </Typography.Text>
                  <Form.Item
                    name="SCHOOL_2026_PT1_Q19"
                    rules={numberInputRules({
                      required: true,
                      allowNegative: false,
                      min: 0,
                      max: 120,
                    })}
                    className="affidavitRowInput"
                  >
                    <Input disabled={disabled || isReviewMode} />
                  </Form.Item>
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "SCHOOL_2026_PT1_AFFIDAVIT_VED_TEACHING_2",
                      def: "years.",
                    })}
                  </Typography.Text>
                </Col>
              </Row>
            </li>
            <Row>
              <Col>
                <li>
                  <Typography.Text>
                    {Text({
                      tid: "SCHOOL_2026_PT1_AFFIDAVIT_INFORMATION_ACCURACY",
                      def: "All the information provided in this application form is completely true.",
                    })}
                  </Typography.Text>
                </li>
              </Col>
              <Col>
                <li>
                  <Typography.Text>
                    {Text({
                      tid: "SCHOOL_2026_PT1_AFFIDAVIT_DOCUMENTS_AUTHENTICITY",
                      def: "All the information in the attached agreements and certificates with this application is true.",
                    })}
                  </Typography.Text>
                </li>
              </Col>
            </Row>
          </ul>
          <Row>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT1_AFFIDAVIT_SIGN_CONFIRMATION",
                def: "I have signed this certificate after verifying all the facts.",
              })}
            </Typography.Text>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT1_AFFIDAVIT_UNDERSTANDING",
                def: "I fully understand the significance and implications of signing this affidavit.",
              })}
            </Typography.Text>
            {!disabled ? (
              <Form.Item
                name="affidavitAccepted"
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    transform: (value: boolean) => value || undefined,
                    type: "boolean",
                    message: Text({
                      tid: "AFFIDAVIT_ACCEPTANCE_REQUIRED",
                      def: "You must accept the affidavit declaration to submit the form",
                    }),
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Checkbox
                  checked={affidavitAccepted}
                  disabled={disabled || isReviewMode}
                >
                  {Text({
                    tid: "SCHOOL_2026_PT1_AFFIDAVIT_DISQUALIFICATION_CONSENT",
                    def: "I acknowledge that if any information in this affidavit is found to be false, this Vedic school, along with all its teachers and students, may be permanently disqualified from the Bharatatma Puraskar.",
                  })}
                </Checkbox>
              </Form.Item>
            ) : (
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_2026_PT1_AFFIDAVIT_DISQUALIFICATION_CONSENT",
                  def: "I acknowledge that if any information in this affidavit is found to be false, this Vedic school, along with all its teachers and students, may be permanently disqualified from the Bharatatma Puraskar.",
                })}
              </Typography.Text>
            )}
          </Row>
          {/* 42) Full Name of the Head of the Institution*/}
          <Row className="QuestionsSubSection affidavitQuestionFirst">
            <Col sm={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q20"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q20",
                  def: "Full Name of the Head of the Institution",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input disabled={disabled || isReviewMode} />
              </Form.Item>
            </Col>
          </Row>

          {/* 44) Date */}
          <Row className="QuestionsSubSection">
            <Col sm={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q21"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q21",
                  def: "Date",
                })}
                rules={inputRequiredBasicRule}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  placeholder={Text({
                    tid: "SELECT_DATE",
                    def: "Ex. 21-05-2024",
                  })}
                  disabled={disabled || isReviewMode}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 45) Place*/}
          <Row className="QuestionsSubSection">
            <Col sm={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT1_Q22"
                label={Text({
                  tid: "SCHOOL_2026_PT1_Q22",
                  def: "Place",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  placeholder={Text({
                    tid: "SCHOOL_2026_PT1_Q22_PLACEHOLDER",
                    def: "Ex. Varanasi",
                  })}
                  disabled={disabled || isReviewMode}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="affidavitSection">
          {/* 23) Signature of the Head of the Institution*/}
          <Row className="QuestionsSubSection">
            <Col sm={24} lg={12} xl={8}>
              <DocumentUploadField
                fieldName="SCHOOL_2026_PT1_Q23"
                label={
                  <>
                    {Text({
                      tid: "SCHOOL_2026_PT1_Q23",
                      def: "Upload signature",
                    })}
                    <FormHelpTooltip
                      title={Text({
                        tid: "SCHOOL_2026_PT1_Q23_TOOLTIP",
                        def: "Please upload a scanned copy or clear photograph of the signature of the Head of the Institution. The signature must be clearly visible and legible. Accepted file formats are PDF, JPG, and PNG.",
                      })}
                    />
                  </>
                }
                formId={formId || ""}
                userId={userId || ""}
                currentStage={eFORMSTATUS.STAGE1INPROGRESS}
                disabled={disabled || isReviewMode}
                existingDocuments={documentsData}
                onUploadComplete={(docId) =>
                  handleFieldChange(
                    "SCHOOL_2026_PT1_Q23",
                    docId,
                    eAnswerType.FILE,
                  )
                }
              />
            </Col>
          </Row>
        </div>

        {/* -------------------------------------------- */}
        {/* SUBMIT BUTTON */}
        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && !isReviewMode && (
            <Button
              block
              type="primary"
              onClick={handleNextClick}
              disabled={isLoading || !affidavitAccepted}
              className="formSubmitButton"
            >
              {Text({ tid: "NEXT", def: "Next" })}
            </Button>
          )}
          {disabled === false && isReviewMode && (
            <div style={{ display: "flex", gap: "16px", width: "100%" }}>
              <Button
                block
                onClick={handleBackClick}
                disabled={isLoading}
                className="formSubmitButton"
              >
                {Text({ tid: "BACK", def: "Back" })}
              </Button>
              <Button
                block
                type="primary"
                htmlType="submit"
                disabled={isLoading || !affidavitAccepted}
                className="formSubmitButton"
                loading={isSubmitting}
              >
                {Text({ tid: "SUBMIT", def: "Submit" })}
              </Button>
            </div>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default SchoolPartOneCurrent;
